/**
 * PersonDrawer: one person on the Salvation Team (the People list, /people/:id, the overview priorities and the review
 * queue's "possible duplicate" links all open it).
 *
 * Shows their status and flags, how to reach them (call, WhatsApp with the welcome typed in, email, copy the welcome),
 * where they decided and how they came in, the permanent Lead and Backup with who is responsible now and why, the
 * deadline and tries, the full history (newest first, never edited), changes to the pair and the journey so far.
 * Converts.Manage gives Log what happened (with the hand-over confirmation), Correct their details, Reopen, Close and
 * Mark as reviewed; Assign gives a first Lead and Backup; Assignments.Correct changes a pair with a reason.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  IconAlertTriangle,
  IconBrandWhatsapp,
  IconBuildingChurch,
  IconCalendar,
  IconCalendarEvent,
  IconChevronDown,
  IconClock,
  IconCopy,
  IconExternalLink,
  IconHistory,
  IconInbox,
  IconMail,
  IconMessagePlus,
  IconPencil,
  IconPhone,
  IconRefresh,
  IconRoute,
  IconUser,
  IconUsers,
  IconX,
  IconEye,
} from '@tabler/icons-react';
import {
  salvationIsFollowUp,
  salvationIsUnreached,
  useSalvationAdminService,
  SALVATION_CONVERT_STATUS as S,
  SALVATION_RESPONSIBILITY_REASON as R,
  type SalvationConvertDetailView,
} from '@actschurch/ccms-sdk';
import { Alert, Badge, Button, Divider, Drawer, Group, Menu, Paper, SimpleGrid, Skeleton, Stack, Text } from '@/components/ui';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useSalvationPermissions } from '@/hooks/salvation/useSalvationPermissions';
import { DetailRow, InlineEmpty } from '../../FormsManagement/components/FormsUi';
import { DrawerSection, InlineLink, LeadBackupCards, LookupBadge, PersonFlags, StatusBadge } from './SalvationUi';
import { AssignModal, EditDetailsModal, LogFollowUpModal, ReasonModal } from './PersonModals';
import { useSalvationLookups, useSalvationTeams } from './useSalvationData';
import { channelFallbackName, cssColour, formatDateTime, formatDay, isPast, plural, relative, salvationErrorMessage, salvationKeys, salvationPaths } from './salvationUtils';

type Dialog = null | 'log' | 'edit' | 'assign' | 'correct' | 'reopen' | 'close' | 'review';

interface PersonDrawerProps {
  convertId: number | null;
  onClose: () => void;
}

/** Why the responsible person is responsible, in a sentence. */
function responsibleSentence(p: SalvationConvertDetailView, reasonName?: string): string {
  if (!p.responsible) return p.lead || p.backup ? 'Nobody is responsible right now.' : 'Nobody is responsible yet: give them a Lead and a Backup.';
  const who = `${p.responsible.displayName} (${p.backupIsResponsible ? 'Backup' : 'Lead'})`;
  switch (p.responsibilityReasonID) {
    case R.DeadlinePassed:
      return `${who} is responsible now: the Lead's deadline passed without reaching them.`;
    case R.TriesUsedUp:
      return `${who} is responsible now: the Lead used up their tries.`;
    case R.LeadNotActive:
      return `${who} is responsible now: the Lead is no longer active on the team.`;
    default:
      return reasonName && p.responsibilityReasonID !== R.Lead ? `${who} is responsible now: ${reasonName.toLowerCase()}.` : `${who} is responsible now.`;
  }
}

export function PersonDrawer({ convertId, onClose }: PersonDrawerProps) {
  const admin = useSalvationAdminService();
  const perms = useSalvationPermissions();
  const teams = useSalvationTeams();
  const { statuses, followUpTypes, reasons, decisionSources, channels, milestones, milestoneStatuses } = useSalvationLookups();
  const [dialog, setDialog] = useState<Dialog>(null);

  const { data: p, isLoading, error } = useQuery({
    queryKey: salvationKeys.person(convertId ?? 0),
    queryFn: () => admin.getPerson(convertId as number),
    enabled: convertId != null,
  });

  const team = p ? teams.byId.get(p.teamID) : undefined;
  const reasonName = p ? reasons.get(p.responsibilityReasonID)?.name : undefined;

  const copyWelcome = async () => {
    if (!p?.welcomeMessage) return;
    try {
      await navigator.clipboard.writeText(p.welcomeMessage);
      toast.success('Copied', 'Paste it into your own message.');
    } catch {
      toast.error("Couldn't copy", 'Select the text and copy it instead.');
    }
  };

  const renderBody = (p: SalvationConvertDetailView) => {
    const inFollowUp = salvationIsFollowUp(p.statusID);
    const unreached = salvationIsUnreached(p.statusID);
    const unassigned = !p.lead || !p.backup;
    const decidedAt = p.decisionSourceID ? decisionSources.get(p.decisionSourceID)?.name : null;
    const maxTries = team?.maxLeadAttempts;
    const overdue = p.isOverdue || (unreached && isPast(p.dueAt));
    const canManage = perms.canManagePeople;

    return (
      <Stack gap="md">
        <Paper withBorder radius="md" p="md">
          <Stack gap="sm">
            <Group justify="space-between" wrap="wrap" gap="xs">
              <Group gap={6} wrap="wrap">
                <StatusBadge statusId={p.statusID} statuses={statuses} />
                <PersonFlags isOverdue={p.isOverdue} backupIsResponsible={p.backupIsResponsible} reviewNeeded={p.reviewNeeded} isEscalated={p.isEscalated} />
                {p.memberID ? <LookupBadge name="CCMS member" color="teal" size="xs" /> : null}
              </Group>
              {canManage && (
                <Group gap="xs">
                  <Button size="compact-sm" leftSection={<IconMessagePlus size={14} />} onClick={() => setDialog('log')}>
                    Log what happened
                  </Button>
                  <Button size="compact-sm" variant="light" leftSection={<IconPencil size={14} />} onClick={() => setDialog('edit')}>
                    Correct details
                  </Button>
                  <Menu position="bottom-end" withinPortal shadow="md">
                    <Menu.Target>
                      <Button size="compact-sm" variant="default" rightSection={<IconChevronDown size={12} />}>
                        More
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {unassigned && perms.canAssign && (
                        <Menu.Item leftSection={<IconUsers size={14} />} onClick={() => setDialog('assign')}>
                          Give a Lead and a Backup
                        </Menu.Item>
                      )}
                      {!unassigned && perms.canCorrectAssignments && (
                        <Menu.Item leftSection={<IconUsers size={14} />} onClick={() => setDialog('correct')}>
                          Correct the assignment
                        </Menu.Item>
                      )}
                      {p.reviewNeeded && (
                        <Menu.Item leftSection={<IconEye size={14} />} onClick={() => setDialog('review')}>
                          Mark as reviewed
                        </Menu.Item>
                      )}
                      {!inFollowUp && (
                        <Menu.Item leftSection={<IconRefresh size={14} />} onClick={() => setDialog('reopen')}>
                          Reopen
                        </Menu.Item>
                      )}
                      {p.statusID !== S.Closed && (
                        <>
                          <Menu.Divider />
                          <Menu.Item color="red" leftSection={<IconX size={14} />} onClick={() => setDialog('close')}>
                            Close (duplicate or privacy request)
                          </Menu.Item>
                        </>
                      )}
                    </Menu.Dropdown>
                  </Menu>
                </Group>
              )}
            </Group>

            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              <DetailRow icon={<IconPhone size={16} />} label="Phone">
                {p.phoneDisplay ? (
                  <InlineLink size="sm" href={`tel:${p.phone ?? p.phoneDisplay}`}>
                    {p.phoneDisplay}
                  </InlineLink>
                ) : (
                  <Text size="sm" c="dimmed">
                    No number
                  </Text>
                )}
              </DetailRow>
              <DetailRow icon={<IconMail size={16} />} label="Email">
                {p.email ? (
                  <InlineLink size="sm" href={`mailto:${p.email}`}>
                    {p.email}
                  </InlineLink>
                ) : (
                  <Text size="sm" c="dimmed">
                    No email
                  </Text>
                )}
              </DetailRow>
              <DetailRow icon={<IconBuildingChurch size={16} />} label="Campus team">
                <Text size="sm">{p.teamName}</Text>
              </DetailRow>
              <DetailRow icon={<IconCalendar size={16} />} label="Decided">
                <Text size="sm">
                  {formatDay(p.decisionDate)}
                  {decidedAt ? ` at ${decidedAt}` : ''}
                </Text>
              </DetailRow>
              <DetailRow icon={<IconInbox size={16} />} label="Came in">
                <Text size="sm">
                  {channels.get(p.channelID)?.name ?? channelFallbackName(p.channelID)}
                  {p.createdAt ? `, ${formatDateTime(p.createdAt)}` : ''}
                </Text>
              </DetailRow>
              <DetailRow icon={<IconUser size={16} />} label="CCMS member">
                {p.memberID ? (
                  <InlineLink size="sm" href={salvationPaths.member(p.memberID)} target="_blank">
                    Open the member profile <IconExternalLink size={12} />
                  </InlineLink>
                ) : (
                  <Text size="sm" c="dimmed">
                    Not linked to a member yet
                  </Text>
                )}
              </DetailRow>
            </SimpleGrid>

            <Group gap="sm" wrap="wrap">
              <Button component="a" href={p.phone ? `tel:${p.phone}` : undefined} size="xs" variant="light" leftSection={<IconPhone size={14} />} disabled={!p.phone && !p.phoneDisplay}>
                Call
              </Button>
              <Button
                component="a"
                href={p.whatsAppLink ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                size="xs"
                color="green"
                variant="light"
                leftSection={<IconBrandWhatsapp size={14} />}
                disabled={!p.whatsAppLink}
              >
                WhatsApp
              </Button>
              <Button size="xs" variant="default" leftSection={<IconCopy size={14} />} onClick={copyWelcome} disabled={!p.welcomeMessage}>
                Copy the welcome
              </Button>
            </Group>
            {p.welcomeMessage && (
              <Paper withBorder p="sm" radius="md">
                <Text size="xs" c="dimmed" mb={4}>
                  The welcome, sent from your own phone
                </Text>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {p.welcomeMessage}
                </Text>
              </Paper>
            )}
          </Stack>
        </Paper>

        {p.statusID === S.Declined && (
          <Alert color="grape" icon={<IconAlertTriangle size={18} />}>
            They could not be reached or declined. Please respect a request not to be contacted.
          </Alert>
        )}
        {p.isEscalated && (
          <Alert color="red" title="Escalated to leaders">
            Nobody has reached them, even after the Backup's deadline. A leader needs to step in.
          </Alert>
        )}

        <DrawerSection title="Lead and Backup" icon={<IconUsers size={16} />}>
          <Stack gap="sm">
            <LeadBackupCards lead={p.lead} backup={p.backup} responsible={p.responsible} reason={reasonName} />
            <Text size="sm">{responsibleSentence(p, reasonName)}</Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              <DetailRow icon={<IconClock size={16} />} label="Deadline">
                <Text size="sm" c={overdue ? 'red' : undefined} fw={overdue ? 600 : undefined}>
                  {p.dueAt ? `${formatDateTime(p.dueAt)} (${relative(p.dueAt)})` : unreached ? 'None yet' : 'Not needed: they were reached'}
                </Text>
              </DetailRow>
              <DetailRow icon={<IconPhone size={16} />} label="Tries">
                <Text size="sm">
                  {maxTries ? `${p.attemptCount} of ${maxTries} before the Backup steps in` : plural(p.attemptCount, 'try', 'tries')}
                </Text>
              </DetailRow>
            </SimpleGrid>
            {unassigned && perms.canAssign && (
              <Group>
                <Button size="xs" variant="light" leftSection={<IconUsers size={14} />} onClick={() => setDialog('assign')}>
                  Give a Lead and a Backup
                </Button>
              </Group>
            )}
            {p.assignmentChanges.length > 0 && (
              <Stack gap={4}>
                <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                  Changes to the pair
                </Text>
                {p.assignmentChanges.map((c) => (
                  <Paper key={c.changeID} withBorder radius="sm" p="xs">
                    <Text size="xs">
                      Lead {c.oldLead ?? 'none'} to {c.newLead ?? 'none'}; Backup {c.oldBackup ?? 'none'} to {c.newBackup ?? 'none'}.
                    </Text>
                    <Text size="xs" c="dimmed">
                      {c.reason}
                      {c.changedByName ? `, by ${c.changedByName}` : ''} · {formatDateTime(c.changedAt)}
                    </Text>
                  </Paper>
                ))}
              </Stack>
            )}
          </Stack>
        </DrawerSection>

        <DrawerSection title="History" icon={<IconHistory size={16} />} count={p.timeline.length}>
          {p.timeline.length === 0 ? (
            <InlineEmpty icon={<IconHistory size={20} />} title="Nothing yet" description="What happens with them shows here, newest first." />
          ) : (
            <Stack gap={0}>
              {p.timeline.map((f, i) => {
                const type = followUpTypes.get(f.typeID);
                return (
                  <Group key={f.followUpID} gap="sm" align="flex-start" wrap="nowrap">
                    <Stack gap={0} align="center" style={{ alignSelf: 'stretch' }}>
                      <div style={{ width: 12, height: 12, borderRadius: 6, marginTop: 4, background: cssColour(type?.color, 'gray') }} />
                      {i < p.timeline.length - 1 && <div style={{ flex: 1, width: 2, background: 'var(--mantine-color-default-border)' }} />}
                    </Stack>
                    <Stack gap={2} pb="md" style={{ minWidth: 0, flex: 1 }}>
                      <Group gap={6} wrap="wrap">
                        <Text size="sm" fw={600}>
                          {type?.name ?? 'Update'}
                        </Text>
                        {f.statusAfter && f.statusBefore !== f.statusAfter && <StatusBadge statusId={f.statusAfter} statuses={statuses} size="xs" />}
                      </Group>
                      {f.note && (
                        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                          {f.note}
                        </Text>
                      )}
                      <Text size="xs" c="dimmed">
                        {f.actorName ?? 'Someone'} · {formatDateTime(f.occurredAt)}
                        {f.source === 'portal' ? ' · portal' : ''}
                      </Text>
                    </Stack>
                  </Group>
                );
              })}
            </Stack>
          )}
        </DrawerSection>

        <DrawerSection title="Journey" icon={<IconRoute size={16} />}>
          <Stack gap={6}>
            {p.reachedAt && <DetailRow icon={<IconPhone size={16} />} label="First spoken to"><Text size="sm">{formatDateTime(p.reachedAt)}</Text></DetailRow>}
            {p.metAt && <DetailRow icon={<IconBuildingChurch size={16} />} label="Met at church"><Text size="sm">{formatDateTime(p.metAt)}</Text></DetailRow>}
            {p.handedOverAt && <DetailRow icon={<IconUsers size={16} />} label="Handed over"><Text size="sm">{formatDateTime(p.handedOverAt)}</Text></DetailRow>}
            {p.connectedAt && <DetailRow icon={<IconUsers size={16} />} label="Connected"><Text size="sm">{formatDateTime(p.connectedAt)}</Text></DetailRow>}
            {p.closedAt && <DetailRow icon={<IconX size={16} />} label="Closed"><Text size="sm">{formatDateTime(p.closedAt)}</Text></DetailRow>}
            {!p.reachedAt && !p.metAt && !p.handedOverAt && !p.closedAt && (
              <Text size="sm" c="dimmed">
                Not reached yet.
              </Text>
            )}
            {p.milestones.length > 0 && (
              <Group gap={6} wrap="wrap" mt={4}>
                {p.milestones.map((m) => {
                  const def = milestones.get(m.milestoneID);
                  const status = milestoneStatuses.get(m.statusID);
                  return (
                    <LookupBadge
                      key={m.milestoneID}
                      name={`${def?.name ?? 'Step'}: ${status?.name ?? ''}`.replace(/: $/, '')}
                      color={def?.color}
                      size="sm"
                      leftSection={<IconCalendarEvent size={10} />}
                    />
                  );
                })}
              </Group>
            )}
          </Stack>
        </DrawerSection>

        <Divider />
        <Text size="xs" c="dimmed">
          History is never edited or removed. The Lead and Backup are permanent; only a correction with a reason changes them.
        </Text>

        <LogFollowUpModal person={p} opened={dialog === 'log'} onClose={() => setDialog(null)} />
        <EditDetailsModal person={p} opened={dialog === 'edit'} onClose={() => setDialog(null)} />
        <AssignModal person={p} mode={dialog === 'correct' ? 'correct' : 'assign'} opened={dialog === 'assign' || dialog === 'correct'} onClose={() => setDialog(null)} />
        <ReasonModal
          person={p}
          kind={dialog === 'close' ? 'close' : dialog === 'review' ? 'review' : 'reopen'}
          opened={dialog === 'reopen' || dialog === 'close' || dialog === 'review'}
          onClose={() => setDialog(null)}
        />
      </Stack>
    );
  };

  return (
    <Drawer
      opened={convertId != null}
      onClose={() => {
        setDialog(null);
        onClose();
      }}
      position="right"
      size="xl"
      title={
        <Group gap="xs" wrap="nowrap">
          <Text fw={600}>{p?.fullName ?? 'Person'}</Text>
          {p && !salvationIsFollowUp(p.statusID) && (
            <Badge size="xs" variant="outline" color="gray" styles={{ root: { textTransform: 'none' } }}>
              Past follow-up
            </Badge>
          )}
        </Group>
      }
    >
      {isLoading ? (
        <Stack gap="sm">
          <Skeleton height={140} radius="md" />
          <Skeleton height={120} radius="md" />
          <Skeleton height={220} radius="md" />
        </Stack>
      ) : error || !p ? (
        <Alert color="red">{salvationErrorMessage(error, 'That person could not be found.')}</Alert>
      ) : (
        renderBody(p)
      )}
    </Drawer>
  );
}
